import { useParams } from "react-router";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ChecklistQueryKeys } from "@/components/Procedures/API/ChecklistQueries";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";
import { ProcedureNodeSteps } from "@/model/Procedure/ProcedureNodeSteps";

import { RouteParams } from "@/service/Utilities";

const updateChecklist = async (
	request: any,
	checklistId: number,
	procedureId: number,
) => {
	await fetch(`/api/checklist/${checklistId}/updateManualChecklist`, {
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request),
	});
};

export const PatchManualChecklist = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: ({
			request,
			checklistId,
			procedureId,
		}: {
			request: any;
			checklistId: number;
			procedureId: number;
		}) => updateChecklist(request, checklistId, procedureId),
		onMutate: ({ checklistId }) => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, +checklistId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				+checklistId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, +variables.checklistId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +variables.checklistId],
			});
		},
		onSuccess: (request, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +variables.checklistId],
			});
		},
	});

	return mutation;
};

export const useSaveChecklistTitle = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const mutation = useMutation({
		mutationFn: (Name: string) => {
			// Optionally perform additional operations like saving to the server
			return Promise.resolve(Name); // Simulate a mutation
		},
		// Update the cache directly on mutation
		onMutate: (Name: string) => {
			const oldData = queryClient.getQueryData([
				ChecklistQueryKeys.checklistPreview,
				checklistId,
			]);

			queryClient.setQueryData(
				[ChecklistQueryKeys.checklistPreview, checklistId],
				{
					...(oldData || {}),
					Name,
				},
			);
		},
	});

	return mutation;
};

export const useSaveChecklistDescription = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const mutation = useMutation({
		mutationFn: (Ingress: string) => {
			// Optionally perform additional operations like saving to the server
			return Promise.resolve(Ingress); // Simulate a mutation
		},
		// Update the cache directly on mutation
		onMutate: (Ingress: string) => {
			const oldData = queryClient.getQueryData([
				ChecklistQueryKeys.checklistPreview,
				checklistId,
			]);

			queryClient.setQueryData(
				[ChecklistQueryKeys.checklistPreview, checklistId],
				{
					...(oldData || {}),
					Ingress,
				},
			);
		},
	});

	return mutation;
};

export const useSaveChecklistStep = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const mutation = useMutation({
		mutationFn: (step: ProcedureNodeStep) => {
			// Optionally perform additional operations like saving to the server
			return Promise.resolve(step); // Simulate a mutation
		},
		// Update the cache directly on mutation
		onMutate: (step: ProcedureNodeStep) => {
			const oldData: InitialData | undefined = queryClient.getQueryData([
				ChecklistQueryKeys.checklistPreview,
				checklistId,
			]);

			queryClient.setQueryData(
				[ChecklistQueryKeys.checklistPreview, checklistId],
				{
					...(oldData || {}),
					Steps: [...(oldData?.Steps || []), step],
				},
			);
		},
	});

	return mutation;
};

export const useUpdateChecklistGroupId = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const mutation = useMutation({
		mutationFn: ({ stepId, groupId }: { stepId: number; groupId: number }) => {
			// Optionally perform additional operations like saving to the server
			return Promise.resolve({ stepId, groupId }); // Simulate a mutation
		},
		// Update the cache directly on mutation
		onMutate: ({ stepId, groupId }: { stepId: number; groupId: number }) => {
			const oldData: ProcedureNodeSteps | undefined = queryClient.getQueryData([
				ChecklistQueryKeys.checklistPreview,
				checklistId,
			]);

			if (oldData) {
				const newSteps = [...(oldData.Steps || [])];
				const existingStep = oldData.Steps?.find((s) => s.Id === stepId);
				if (existingStep) {
					const index = oldData.Steps?.indexOf(existingStep);
					if (index !== undefined) {
						newSteps[index].GroupId = groupId;
					}
				}

				queryClient.setQueryData(
					[ChecklistQueryKeys.checklistPreview, checklistId],
					{
						...oldData,
						Steps: newSteps,
					},
				);
			}
		},
	});

	return mutation;
};

export const useUpdateChecklistSteps = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId;

	const mutation = useMutation({
		mutationFn: (step: ProcedureNodeStep) => {
			// Optionally perform additional operations like saving to the server
			return Promise.resolve(step); // Simulate a mutation
		},
		// Update the cache directly on mutation
		onMutate: (step: ProcedureNodeStep) => {
			const oldData: ProcedureNodeSteps | undefined = queryClient.getQueryData([
				ChecklistQueryKeys.checklistPreview,
				checklistId,
			]);

			if (oldData) {
				const existingStep = oldData.Steps?.find((s) => s.Id === step.Id);
				const newSteps = [...(oldData.Steps || [])];
				if (existingStep) {
					const index = oldData.Steps?.indexOf(existingStep);
					if (index !== undefined) {
						newSteps[index] = step;
					}
				}

				queryClient.setQueryData(
					[ChecklistQueryKeys.checklistPreview, checklistId],
					{
						...oldData,
						Steps: newSteps,
					},
				);
			}
		},
	});

	return mutation;
};

export const useUpdateChecklistStepPreview = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId;

	const mutation = useMutation({
		mutationFn: ({
			index,
			step,
		}: {
			index: number;
			step: ProcedureNodeStep;
		}) => {
			// Optionally perform additional operations like saving to the server
			return Promise.resolve({ index, step }); // Simulate a mutation
		},
		// Update the cache directly on mutation
		onMutate: ({ index, step }: { index: number; step: ProcedureNodeStep }) => {
			const oldData: ProcedureNodeSteps | undefined = queryClient.getQueryData([
				ChecklistQueryKeys.checklistPreview,
				checklistId,
			]);

			if (oldData) {
				const newSteps = [...(oldData.Steps || [])];
				const existingStep = oldData.Steps?.find((s) => s.Id === step.Id);
				if (existingStep) {
					const index = oldData.Steps?.indexOf(existingStep);
					if (index !== undefined) {
						newSteps[index] = step;
					}
				}

				queryClient.setQueryData(
					[ChecklistQueryKeys.checklistPreview, checklistId],
					{
						...oldData,
						Steps: newSteps,
					},
				);
			}
		},
	});

	return mutation;
};

interface InitialData {
	Name: string;
	Ingress: string;
	Steps?: Step[];
}

export type Step = {
	title: string;
	description: string;
	type: string;
	ParentId?: number;
	manualMeasurement?: {
		tag: string;
		value: string;
		unit: string;
	};
	SATOSLiveListener?: {
		tag: string;
		value: number;
	};
};
